/**
 * Hook de tema (claro/oscuro) — Design System v4 "Falcon".
 *
 * Fuente única de verdad del tema visible: el atributo `data-theme`
 * en <html>, que conmuta TODOS los tokens v4 (ver styles/tokens.css).
 * Además sincroniza la clase `.light`/`.dark` para que Tailwind
 * (`darkMode: ["class"]`) y los estilos legacy sigan coherentes.
 *
 * - Default: CLARO.
 * - Respeta `prefers-color-scheme` en el primer arranque (sin preferencia guardada).
 * - Persiste en localStorage (`xm-minotauro-theme`).
 */

import { useState, useEffect, useCallback } from 'react';

const THEME_KEY = 'xm-minotauro-theme';

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  // Sin preferencia guardada → respeta el SO; default claro.
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const applyThemeToDocument = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
};

export const useTheme = () => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyThemeToDocument(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };
};

export default useTheme;
